import { useState } from "react";
import { type FieldErrorDto, usePublishMarketDialogueMutation } from "../../autogen/bff-api";
import { useApiError } from "../../common/errors/useApiError";
import { flushPendingSaves, type PendingSaves } from "../../common/input/pendingSaves";
import { doffinRefusal } from "../sourcing-event/result-notice/DoffinRefusalAlert";
import { type DoffinNoAnswer, doffinNoAnswer } from "../sourcing-event/result-notice/doffinNoAnswer";
import { isChangedElsewhere } from "./changedElsewhere";

export interface MarketDialoguePublishing {
  publish: () => Promise<void>;
  isPublishing: boolean;
  refusalBreaches: FieldErrorDto[] | null;
  noAnswer: DoffinNoAnswer | null;
  isCheckingWhatDoffinGot: boolean;
}

export const useMarketDialoguePublish = ({
  marketDialogueId,
  pendingSaves,
  readLatest,
  contentRevision,
  onRefusedAsChangedElsewhere,
}: {
  marketDialogueId: string | undefined;
  pendingSaves: PendingSaves;
  readLatest: () => Promise<{ data?: { contentRevision: number }; isError: boolean }>;
  contentRevision: () => number;
  onRefusedAsChangedElsewhere: () => void;
}): MarketDialoguePublishing => {
  const { trigger } = useApiError();
  const [publishMutation, { isLoading }] = usePublishMarketDialogueMutation();
  const [isFlushing, setFlushing] = useState(false);
  const [refusalBreaches, setRefusalBreaches] = useState<FieldErrorDto[] | null>(null);
  const [noAnswer, setNoAnswer] = useState<DoffinNoAnswer | null>(null);
  const [isCheckingWhatDoffinGot, setCheckingWhatDoffinGot] = useState(false);

  const whileChecking = async <T,>(read: () => Promise<T>): Promise<T> => {
    setCheckingWhatDoffinGot(true);
    try {
      return await read();
    } finally {
      setCheckingWhatDoffinGot(false);
    }
  };

  const reportNothingSentUnlessChangedElsewhere = async (sentRevision: number) => {
    const latest = await whileChecking(readLatest);
    if (latest.isError || (latest.data?.contentRevision ?? sentRevision) > sentRevision) onRefusedAsChangedElsewhere();
    else setNoAnswer("nothingSent");
  };

  const publish = async () => {
    if (!marketDialogueId) return;
    setRefusalBreaches(null);
    setNoAnswer(null);
    setFlushing(true);
    try {
      await flushPendingSaves(pendingSaves);
    } finally {
      setFlushing(false);
    }
    const sentRevision = contentRevision();
    try {
      await publishMutation({
        marketDialogueId,
        publishMarketDialogueRequest: { contentRevision: sentRevision },
      }).unwrap();
    } catch (e) {
      if (isChangedElsewhere(e)) {
        onRefusedAsChangedElsewhere();
        return;
      }
      const breaches = doffinRefusal(e);
      const unanswered = doffinNoAnswer(e);
      if (breaches) setRefusalBreaches(breaches);
      else if (unanswered === "nothingSent") await reportNothingSentUnlessChangedElsewhere(sentRevision);
      else if (unanswered) setNoAnswer(unanswered);
      else trigger(e);
      if (unanswered === "whileSending") await whileChecking(readLatest);
    }
  };

  return { publish, isPublishing: isFlushing || isLoading, refusalBreaches, noAnswer, isCheckingWhatDoffinGot };
};
